/**
 * Straight walls: the collinear leaves the wall walk cuts a wall into (at
 * every door, window, and opening), joined back up, with each opening's start
 * and end along the run. What snapping reads, in both editors: a room's wall
 * is one line to line up with, openings and all.
 */
import type { FloorPlanRoom, Point } from '@kaiizen/kai-floor-plan';
import { type WallLeaf, wallWalk } from './wallWalk';

export interface WallRun {
  /** The run's first leaf: its inner start and line frame are the run's. */
  first: WallLeaf;
  length: number;
  /** Each opening's start and end along the run, from the first leaf's inner start. */
  openings: number[];
}

const near = (a: Point, b: Point): boolean => Math.hypot(a.x - b.x, a.y - b.y) < 1e-6;
const sameDirection = (a: Point, b: Point): boolean => a.x * b.x + a.y * b.y > Math.cos(Math.PI / 180);

export function wallRuns(room: FloorPlanRoom): WallRun[] {
  const runs: WallRun[] = [];
  let end: Point | null = null;
  for (const leaf of wallWalk(room)) {
    const openings = leaf.cutouts.flatMap(cutout => [cutout.s0, cutout.s1]);
    const last = runs[runs.length - 1];
    if (last && end && sameDirection(last.first.line.dir, leaf.line.dir) && near(end, leaf.innerStart)) {
      last.openings.push(...openings.map(s => s + last.length));
      last.length += leaf.line.length;
    } else {
      runs.push({ first: leaf, length: leaf.line.length, openings });
    }
    end = leaf.innerEnd;
  }
  // The walk may start mid-wall: then its last run continues into its first.
  const first = runs[0], last = runs[runs.length - 1];
  if (runs.length > 1 && end && sameDirection(last.first.line.dir, first.first.line.dir) && near(end, first.first.innerStart)) {
    first.openings = [...last.openings, ...first.openings.map(s => s + last.length)];
    first.length += last.length;
    first.first = last.first;
    runs.pop();
  }
  return runs;
}
